<template>
  <nav class="shelf" aria-label="Catalogs you're comparing">
    <div class="shelf__tabs" role="tablist">
      <div
        v-for="item in items"
        :key="item.id"
        class="shelf__tab"
        :class="[`shelf__tab--tone-${item.tone}`, { 'shelf__tab--active': item.id === activeId }]"
      >
        <button
          type="button"
          role="tab"
          class="shelf__open"
          :aria-selected="item.id === activeId ? 'true' : 'false'"
          :title="`View ${item.college}, ${item.edition}`"
          @click="$emit('select', item.id)"
        >
          <CatalogRibbon class="shelf__ribbon" :tone="item.tone" size="lg" :fill="fillFor(item.id)" />
          <span class="shelf__text">
            <span class="shelf__college">{{ item.college }}</span>
            <span class="shelf__meta">
              {{ item.edition }}<template v-if="statusLine(item.id)"> · {{ statusLine(item.id) }}</template>
            </span>
          </span>
        </button>
        <button
          v-if="comparing"
          type="button"
          class="shelf__remove"
          :aria-label="`Remove ${item.college} ${item.edition} from the comparison`"
          :title="'Remove from comparison'"
          @click="$emit('remove', item.id)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>

      <!-- Waiting for the user to pick a catalog in the sidebar. -->
      <div v-if="adding" class="shelf__slot" :class="`shelf__tab--tone-${nextTone}`" aria-live="polite">
        <CatalogRibbon class="shelf__ribbon" :tone="nextTone" size="lg" hollow />
        <span class="shelf__text">
          <span class="shelf__college">Choose a catalog</span>
          <span class="shelf__meta">In the panel on the left · {{ items.length + 1 }} of {{ max }}</span>
        </span>
        <button type="button" class="ds-link shelf__cancel" @click="$emit('cancel')">Cancel</button>
      </div>

      <button
        v-else-if="items.length < max"
        type="button"
        class="shelf__add"
        @click="$emit('add')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        {{ comparing ? "Add a catalog" : "Compare with another catalog" }}
      </button>
    </div>
  </nav>
</template>

<script>
// The row of catalogs above the viewer. With one catalog it invites a
// comparison; with two or three, each tab switches the viewer to that catalog
// and its ribbon fills as the catalog is read for AI chat.
import CatalogRibbon from "./CatalogRibbon.vue";
import { readStatus } from "@/ai/catalogStore";

export default {
  name: "CompareShelf",
  components: { CatalogRibbon },
  props: {
    // [{ id, college, edition, tone }]
    items: { type: Array, required: true },
    activeId: { type: [String, Number], default: null },
    adding: { type: Boolean, default: false },
    nextTone: { type: Number, default: 1 },
    max: { type: Number, default: 3 },
  },
  emits: ["select", "remove", "add", "cancel"],
  computed: {
    comparing() {
      return this.items.length > 1;
    },
  },
  methods: {
    fillFor(id) {
      const s = readStatus(id);
      if (s.state === "ready") return 100;
      if (s.state !== "reading" || !s.total) return 0;
      return Math.round((s.done / s.total) * 100);
    },
    // Only worth saying while it's not ready; a ready catalog needs no label.
    statusLine(id) {
      const s = readStatus(id);
      if (s.state === "reading") return s.total ? `reading ${Math.round((s.done / s.total) * 100)}%` : "loading";
      if (s.state === "too-large") return "too large for AI";
      if (s.state === "error") return "couldn't be read";
      return "";
    },
  },
};
</script>

<style scoped>
.shelf {
  min-width: 0;
}
.shelf__tabs {
  display: flex;
  align-items: stretch;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 2px;
}
.shelf__tabs::-webkit-scrollbar {
  display: none;
}
.shelf__tab,
.shelf__slot {
  position: relative;
  display: flex;
  align-items: stretch;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 300px;
  border-radius: 12px;
  background: var(--paper);
  border: 1px solid var(--line);
  transition: border-color 150ms ease, box-shadow 150ms ease;
}
.shelf__tab:hover {
  border-color: var(--hair);
}
.shelf__tab--active {
  border-color: var(--ink);
  box-shadow: var(--shadow-card);
}
.shelf__tab--tone-0 {
  --tone: var(--brand-purple);
}
.shelf__tab--tone-1 {
  --tone: var(--brand-pink);
}
.shelf__tab--tone-2 {
  --tone: var(--brand-orange);
}
.shelf__open {
  appearance: none;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  padding: 0 4px 9px 14px;
  border-radius: 12px;
}
.shelf__open:focus-visible,
.shelf__remove:focus-visible,
.shelf__add:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}
/* The ribbon hangs from the tab's top edge, like a bookmark out of a book. */
.shelf__ribbon {
  margin-top: -1px;
}
.shelf__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: 8px;
}
.shelf__college {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.shelf__meta {
  font-size: 12px;
  color: var(--mute);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.shelf__remove {
  appearance: none;
  align-self: flex-start;
  margin: 6px 6px 0 0;
  width: 24px;
  height: 24px;
  flex: none;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 999px;
  background: none;
  color: var(--faint);
  cursor: pointer;
}
.shelf__remove:hover {
  background: var(--wash);
  color: var(--ink);
}
.shelf__remove svg,
.shelf__add svg {
  width: 14px;
  height: 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}
.shelf__slot {
  align-items: flex-start;
  gap: 10px;
  padding: 0 12px 9px 14px;
  border-style: dashed;
  border-color: var(--tone);
  background: color-mix(in oklab, var(--tone) 4%, var(--paper));
}
.shelf__cancel {
  align-self: center;
  margin-left: 6px;
  font-size: 13px;
}
.shelf__add {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  padding: 0 14px;
  min-height: 44px;
  border-radius: 12px;
  border: 1px dashed var(--hair);
  background: transparent;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-soft);
  cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease, background 150ms ease;
}
.shelf__add:hover {
  border-color: var(--ink);
  color: var(--ink);
  background: var(--paper);
}
</style>
